import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, Stack } from '@mui/material';
import HeadTitle from '../../components/HeadTitle';
import Loader from '../../components/common/Loader/Loader';
import { getMessagingActivity } from '../../api/messagingActivity';
import styles from './styles';
import { SAMPLE_MEMBERS } from './sampleData';
import { STATUS_DESCRIPTION, STATUS_LABEL, STATUS_ORDER, statusLabel } from '../../utils/memberStatus';

/**
 * Member Messaging Activity — list page (v6 design).
 *
 * Native render (no iframe) scoped under `.favor-ma`, mirroring the Channel
 * Attribution / Member Engagement tabs. Per-member messaging is framed by WHO
 * STARTED the conversation and whether it got a reply: outbound = conversations the
 * member started; inbound = conversations someone started with them. Every figure on
 * the tab is a conversation count — the distinct-people counts the API still returns
 * (`pm`/`pmh`) are deliberately not rendered. Figures are all-time; `joined` scopes
 * which members are listed by signup date.
 *
 * Live data: GET /admin/analytics/stat/getMessagingActivity. Filtering, sorting
 * and pagination are ALL server-side — the page returns one page of rows plus
 * `totals` computed over the whole filtered population, so the headline tiles
 * describe the population and never an arbitrary page slice. On any failure we
 * fall back to the mockup sample so the tab never renders blank.
 *
 * The v6 tiles/columns need reply-split fields the API may not ship yet
 * (`snap.orep`, `snap.irep`, `snap.outMin`, `snap.inMin`, member `account`, and
 * the matching population `totals`). Everything degrades gracefully to "—" when
 * a field is absent; see API_CHANGES.md for the contract the backend must meet.
 */
// `joined` is the tab's ONLY date control: it picks which members are listed, by
// signup date. Messaging figures are always all-time — the table answers "what has
// this member done", and the per-member page breaks that down by period.
const JOINED = [['7d', 'Last 7 days'], ['30d', 'Last 30 days'], ['90d', 'Last 90 days'], ['all', 'All time'], ['custom', 'Custom']];
// Keys name the measure they order by. The server still accepts the old `pm`/`pmh`
// aliases for bookmarked URLs, but those names meant the distinct-people counts this tab
// no longer shows, so nothing here should use them.
const SORTS = { active: 'Recently Active', oreached: 'Most conversations started', reached: 'Most started with them', rr: 'Highest reply rate' };
// Mirrors ACCOUNT_STATES in MessagingActivityController — the seven canonical statuses
// (utils/memberStatus.js) — minus `ghost` (signup not finished), which is filtered out of
// the list server-side (WEB-40), so its pill would only ever render an empty table.
// `inactive` is the canonical status (was complete, photo removed / below 100%); the old
// 30-day "not seen in a while" overlay that used this name is gone. "Active" is not a
// status: ?account=active selects the Approved + Hidden total.
// Hidden / Deactivated / Suspended / Deleted all STAY: those members may well have
// interacted with someone, and closing or hiding an account does not unsend those messages.
const ACCOUNTS = [
  ['All', 'All'], ['active', STATUS_LABEL.active],
  ...STATUS_ORDER.filter((k) => k !== 'ghost').map((k) => [k, STATUS_LABEL[k]]),
];
const PAGE_SIZE = 50;

const iso = (d) => d.toISOString().slice(0, 10);
const TODAY = iso(new Date());
const shiftDays = (isoStr, n) => { const d = new Date(isoStr); d.setDate(d.getDate() + n); return iso(d); };
const fmtDate = (s) => new Date(`${s}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const fmtClock = (isoStr) => (isoStr ? new Date(isoStr).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const rrClass = (rr) => (rr >= 60 ? 'hi' : rr >= 30 ? 'mid' : 'lo');
const genderChip = (g) => (g === 'F' ? <span className="gchip f">Female</span> : g === 'M' ? <span className="gchip m">Male</span> : <span className="gchip o">Other</span>);
const joinedLabel = (d) => (d === 0 ? 'Today' : d === 1 ? 'Yesterday' : `${d} days ago`);
const pct = (a, b) => (b ? Math.round((a / b) * 100) : null);

// Gender-aware pronouns for the current gender filter — copy throughout the page
// adapts so an all-female selection reads "she / her", all-male "he / him", and a
// mixed selection stays neutral.
const pronouns = (g) => (g === 'f'
  ? { subj: 'she', Subj: 'She', obj: 'her', Obj: 'Her', poss: 'her', were: 'was' }
  : g === 'm'
    ? { subj: 'he', Subj: 'He', obj: 'him', Obj: 'Him', poss: 'his', were: 'was' }
    : { subj: 'they', Subj: 'They', obj: 'them', Obj: 'Them', poss: 'their', were: 'were' });

// Compact duration for median reply times (minutes → m / h / d).
const fmtMin = (x) => {
  if (x == null) return '—';
  if (x < 60) return `${x}m`;
  if (x < 1440) return `${Math.round((x / 60) * 10) / 10}h`;
  return `${Math.round((x / 1440) * 10) / 10}d`;
};
const medianOf = (arr) => {
  const s = arr.filter((v) => v != null).sort((a, b) => a - b);
  if (!s.length) return null;
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : Math.round((s[m - 1] + s[m]) / 2);
};

const acctPill = (st) => {
  if (!STATUS_LABEL[st]) return null;
  return <span className={`acct-st ${st}`} title={STATUS_DESCRIPTION[st]}><i />{statusLabel(st)}</span>;
};

const MessagingActivity = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [joined, setJoined] = useState('30d');
  const [from, setFrom] = useState(shiftDays(TODAY, -29));
  const [to, setTo] = useState(TODAY);
  const [gender, setGender] = useState('all');
  const [location, setLocation] = useState('All');
  const [country, setCountry] = useState('All');
  const [status, setStatus] = useState('All');
  const [account, setAccount] = useState('All');
  const [sort, setSort] = useState('active');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [openMenu, setOpenMenu] = useState('');

  const [data, setData] = useState(null);
  const [statusFlag, setStatusFlag] = useState('loading'); // loading | live | placeholder
  const [fetching, setFetching] = useState(true);
  const barRef = useRef(null);

  // Every filter is a server param now, so build the query in one place.
  // `from`/`to` only travel with joined=custom, so a stale custom range can't
  // silently narrow a keyword window.
  const query = useMemo(() => ({
    joined,
    ...(joined === 'custom' ? { from, to } : {}),
    sort,
    gender: gender === 'all' ? undefined : gender === 'f' ? 'female' : 'male',
    country: country === 'All' ? undefined : country,
    city: location === 'All' ? undefined : location,
    verified: status === 'Verified' ? true : undefined,
    account: account === 'All' ? undefined : account,
    searchTerm: debouncedQ.trim() || undefined,
  }), [joined, from, to, sort, gender, country, location, status, account, debouncedQ]);

  const joinedText = joined === 'custom'
    ? `${fmtDate(from)} – ${fmtDate(to)}`
    : JOINED.find(([k]) => k === joined)[1].toLowerCase();

  // Any filter change invalidates the current page number. Reset during render
  // rather than in an effect: an effect would run AFTER the fetch effect below
  // had already fired a request pairing the new filters with the stale page.
  const queryKey = JSON.stringify(query);
  const [pageKey, setPageKey] = useState(queryKey);
  const [page, setPage] = useState(1);
  if (queryKey !== pageKey) {
    setPageKey(queryKey);
    setPage(1);
  }

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let active = true;
    setFetching(true);
    (async () => {
      try {
        const res = await getMessagingActivity({ ...query, page, limit: PAGE_SIZE });
        if (!active) return;
        if (res && Array.isArray(res.members) && res.totals) {
          setData(res);
          setStatusFlag('live');
        } else {
          setData(null);
          setStatusFlag('placeholder');
        }
      } catch (err) {
        if (active) { setData(null); setStatusFlag('placeholder'); }
      } finally {
        if (active) setFetching(false);
      }
    })();
    return () => { active = false; };
  }, [query, page]);

  // Close dropdowns on outside click.
  useEffect(() => {
    const onDoc = (e) => { if (barRef.current && !barRef.current.contains(e.target)) setOpenMenu(''); };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  const live = statusFlag === 'live' && data;
  // The server could produce neither live nor snapshot metrics for these rows, so every
  // per-member number in the payload is a zeroed placeholder. Render them as "—".
  const metricsOff = !!live && data.rowMetrics === 'unavailable';
  const rows = live ? data.members : SAMPLE_MEMBERS;
  const total = live ? data.total : SAMPLE_MEMBERS.length;
  const pages = live ? data.pages : 1;
  const P = pronouns(gender);

  // Country / City options, each with the number of members choosing it would list.
  //
  // The counts come from the server and describe THIS selection — the joined window,
  // gender, verified, account and search that are already applied — so the number on an
  // option is the number of rows you get by clicking it. "All" carries the same total the
  // header does. (They used to be whole-population figures carried on the messaging
  // snapshot, which also meant the menus emptied out entirely whenever that snapshot was
  // still building.) The city list narrows to the selected country.
  //
  // Both are capped server-side (`city` is free text, ~4.2k mostly-junk distinct values);
  // the long tail stays reachable through the search box, which matches on city.
  const locations = useMemo(
    () => [{ name: 'All', n: total }, ...(live ? data.cities || [] : [])],
    [live, data, total],
  );
  const countries = useMemo(() => {
    if (live && Array.isArray(data.countries) && data.countries.length) {
      return [{ name: 'All', n: total }, ...data.countries];
    }
    // Sample/placeholder mode only — the whole dataset is on the page, so the distinct
    // countries of the rows ARE the full list.
    const counts = new Map();
    rows.forEach((m) => { if (m.country) counts.set(m.country, (counts.get(m.country) || 0) + 1); });
    return [
      { name: 'All', n: total },
      ...[...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([name, n]) => ({ name, n })),
    ];
  }, [live, data, rows, total]);

  // Headline tiles. Prefer the server's population `totals`; in sample/placeholder
  // mode the whole dataset is on the page, so summing rows is exact. When live but
  // the backend hasn't shipped the reply-split totals yet, leave them null so the
  // tiles read "—" rather than a misleading single-page sum.
  const cards = useMemo(() => {
    const t = live ? data.totals : null;
    const hasNew = t && t.sentPeople != null;
    if (hasNew) {
      return {
        sentPeople: t.sentPeople, sentMsgs: t.sentMsgs, outRepliers: t.outRepliers,
        outReached: t.outReached ?? null,
        recvPeople: t.recvPeople, recvMsgs: t.recvMsgs, inRepliers: t.inRepliers,
        // Inbound CONVERSATIONS — the denominator that matches inRepliers and the
        // per-row column. Falls back to recvPeople (people who messaged them) only
        // for a backend that predates the field.
        inReached: t.inReached ?? t.recvPeople ?? null,
        medOutMin: t.medOutMin ?? null, medInMin: t.medInMin ?? null, complete: true,
      };
    }
    if (!live) {
      const sum = (f) => rows.reduce((a, m) => a + (m.snap?.[f] || 0), 0);
      return {
        sentPeople: sum('pm'), sentMsgs: sum('ms'), outRepliers: sum('outboundReplied'),
        outReached: sum('oreached'),
        recvPeople: sum('pmh'), recvMsgs: sum('mr'), inRepliers: sum('irep'),
        inReached: sum('reached'),
        medOutMin: medianOf(rows.map((m) => m.snap?.outMin)),
        medInMin: medianOf(rows.map((m) => m.snap?.inMin)), complete: true,
      };
    }
    return { complete: false };
  }, [live, data, rows]);

  const goDetail = useCallback((id) => navigate(`/messaging-activity/${id}`), [navigate]);
  const openDetailNewTab = useCallback((id) => window.open(`/messaging-activity/${id}`, '_blank', 'noopener,noreferrer'), []);

  // Export the whole filtered population, not just the page on screen.
  //
  // Deliberately ONE request. Paging the export across several requests lets a
  // background snapshot refresh land mid-export: the sort keys change, rows shift
  // across page boundaries, and the file ends up with members duplicated or missing.
  // One request reads one snapshot. EXPORT_LIMIT is the server's cap; if the
  // population ever outgrows it the file would truncate silently, so say so.
  const [exporting, setExporting] = useState(false);
  const EXPORT_LIMIT = 50000;
  const exportCsv = async () => {
    setExporting(true);
    try {
      const all = await getMessagingActivity({ ...query, page: 1, limit: EXPORT_LIMIT });
      // A bulk export is too large to recompute live (see LIVE_ROW_LIMIT server-side), so
      // it reads the population snapshot — and in the first minutes after a server restart
      // there ISN'T one yet. Every metric would then be a confident 0 in a file that looks
      // complete. The on-screen rows are unaffected: they are always recomputed live.
      if (all?.warming) {
        window.alert('Messaging figures are still being computed after a server restart. The rows on screen are live, but a full export needs the population snapshot — try again in a minute.');
        return;
      }
      if (all?.truncated || (all && all.total > all.members.length)) {
        window.alert(`This export would be incomplete: ${all.total.toLocaleString()} members match but only ${all.members.length.toLocaleString()} can be exported at once. Narrow the filters and try again.`);
        return;
      }
      const list = all?.members?.length ? all.members : rows;
      const head = ['Member', 'Gender', 'Account', 'City', 'Country', 'Joined (days ago)', 'People Messaged', 'Messages Sent', 'Replied To Them', 'Conversations They Opened', 'Outbound Reply Rate %', 'Median Outbound Reply (min)', 'People Who Messaged', 'Messages Received', 'They Replied', 'Inbound Conversations', 'Inbound Reply Rate %', 'Median Inbound Reply (min)', 'Profile Likes'];
      const lines = list.map((m) => {
        const s = m.snap || {};
        return [m.name, m.gender, m.account ? statusLabel(m.account) : '', m.city, m.country, m.joinedDaysAgo, s.pm, s.ms, s.outboundReplied ?? '', s.oreached ?? 0, s.orr ?? '', s.outMin ?? '', s.pmh, s.mr, s.irep ?? s.replied ?? '', s.reached ?? '', s.rr, s.inMin ?? '', s.likes];
      });
      const csv = [head, ...lines].map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `messaging-activity-${joined === 'custom' ? `${from}_to_${to}` : joined}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const toggleMenu = (id) => (e) => { e.stopPropagation(); setOpenMenu((m) => (m === id ? '' : id)); };
  const pick = (setter) => (val) => { setter(val); setOpenMenu(''); };
  // City is scoped to the country (the server narrows the City options to it), so a city
  // left over from the previous country would be a filter pair that matches nobody.
  const pickCountry = (val) => { setCountry(val); setLocation('All'); setOpenMenu(''); };
  // "Clear all" means ALL of them, Joined included — it sat in the same bar as the rest
  // and left the signup window wherever it was, so clearing a 90-day view still showed a
  // 90-day population under filters that all read "All".
  const clearAll = () => {
    setGender('all'); setCountry('All'); setLocation('All'); setStatus('All');
    setAccount('All'); setSort('active'); setQ('');
    setJoined('30d'); setFrom(shiftDays(TODAY, -29)); setTo(TODAY);
  };

  // Compact pager: first, last, and a window around the current page.
  const pageList = useMemo(() => {
    const out = [];
    for (let i = 1; i <= pages; i++) {
      if (i === 1 || i === pages || Math.abs(i - page) <= 1) out.push(i);
      else if (out[out.length - 1] !== '…') out.push('…');
    }
    return out;
  }, [pages, page]);

  const who = `${total.toLocaleString()} ${gender === 'f' ? 'women' : gender === 'm' ? 'men' : 'members'}`;

  return (
    <Box sx={{ pb: 4 }}>
      <style>{styles}</style>

      <Stack direction="row" justifyContent={embedded ? 'flex-end' : 'space-between'} alignItems="flex-start" sx={{ mb: embedded ? 0 : 2 }}>
        {!embedded && (
          <HeadTitle
            imageSrc="/assets/images/admin-panel/analytics.png"
            text="Member Messaging Activity"
            description="Per-member outbound & inbound conversations, who started them & who replied"
          />
        )}
        {statusFlag === 'placeholder' && (
          <Chip size="small" color="warning" variant="outlined" label="Showing sample data — live source unavailable" />
        )}
      </Stack>

      <div className="favor-ma">
        {fetching ? (
          <div style={{ minHeight: 'calc(100vh - 240px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader />
          </div>
        ) : (
          <>
            {/* The ROWS are recomputed live on every request, so they always agree with
                the member's own detail page. The five TILES above cannot be — they score
                the whole population, which is a full chat-store scan — so they come from
                a cached snapshot and say when it was taken. The distinction is worth
                stating: it is the difference between a number you can cross-check
                against another page and one you cannot. */}
            {/* Says WHERE the row numbers came from. It must never claim "rows are live"
                on a response whose rowMetrics is 'unavailable' — those rows are all-zero
                placeholders (no snapshot AND the live pass failed), and calling them live
                is the fabricated-numbers failure this tab exists to avoid. */}
            <div className="stats-ctx">
              {total ? `Figures summarise all ${who} joined ${joinedText}` : 'No members in the current selection'}
              {live && metricsOff
                ? ' · per-member figures are unavailable right now (the server is rebuilding them) — everything else on this row is correct'
                : live && data.rowMetrics === 'snapshot'
                  ? ` · rows from the snapshot of ${fmtClock(data.snapshotAt)}`
                  : live && data.snapshotAt
                    ? ` · rows are live · totals as of ${fmtClock(data.snapshotAt)}`
                    : live && data.warming
                      ? ' · rows are live · population totals still building, refresh in a moment'
                      : ''}
            </div>
            <section className="stats s5">
              {/* Headline is the CONVERSATION count, matching the rows, the detail cards and
                  the reply-rate tile beside it — whose denominator this now IS. It used to be
                  Σ distinct people, so the tile read 7,145 above a neighbour saying "of
                  5,270 conversations", two different measures one inch apart. */}
              <div className="stat"><span className="bar" /><div className="k">Conversations Started</div>
                <div className="v">{cards.complete && cards.outReached != null ? cards.outReached.toLocaleString() : '—'}</div>
                <div className="d">{cards.complete ? `${P.subj} sent the first message · ${cards.sentMsgs.toLocaleString()} messages sent` : 'awaiting live totals'}</div></div>
              <div className="stat s5"><span className="bar" /><div className="k">Outbound Reply Rate</div>
                <div className="v">{cards.complete && cards.outReached ? `${pct(cards.outRepliers, cards.outReached)}%` : '—'}</div>
                <div className="d">{cards.complete && cards.outReached ? `${cards.outRepliers.toLocaleString()} of ${cards.outReached.toLocaleString()} conversations ${P.subj} opened got a reply · median ${fmtMin(cards.medOutMin)}` : ''}</div></div>
              <div className="stat s3"><span className="bar" /><div className="k">Started With {P.Obj}</div>
                <div className="v">{cards.complete && cards.inReached != null ? cards.inReached.toLocaleString() : '—'}</div>
                <div className="d">{cards.complete ? `the other person sent the first message · ${cards.recvMsgs.toLocaleString()} messages received` : ''}</div></div>
              {/* Both tiles count inbound CONVERSATIONS (threads the other person
                  opened) — the same denominator the table's Inbound Reply Rate
                  column uses. They used to divide by people-who-messaged-them,
                  which also counts threads the member opened, so the rate read low
                  and "Awaiting Reply" read high against the very rows below. */}
              <div className="stat s3"><span className="bar" /><div className="k">Inbound Reply Rate</div>
                <div className="v">{cards.complete && cards.inReached ? `${pct(cards.inRepliers, cards.inReached)}%` : '—'}</div>
                <div className="d">{cards.complete && cards.inReached ? `${P.subj} answered ${cards.inRepliers.toLocaleString()} of ${cards.inReached.toLocaleString()} inbound conversations · median ${fmtMin(cards.medInMin)}` : ''}</div></div>
              <div className="stat s2"><span className="bar" /><div className="k">Awaiting Reply</div>
                <div className="v">{cards.complete && cards.inReached != null ? Math.max(0, cards.inReached - cards.inRepliers).toLocaleString() : '—'}</div>
                <div className="d">{cards.complete ? `inbound conversations awaiting ${P.poss} reply` : ''}</div></div>
            </section>

            <div className="search">
              <span className="mag" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, city, member ID…" />
            </div>

            <div className="filterbar" ref={barRef}>
              <div className="fgroup">
                <span className="flabel">Gender</span>
                <div className="seg">
                  {[['all', 'All'], ['f', 'Female'], ['m', 'Male']].map(([k, l]) => (
                    <button key={k} type="button" className={`opt ${gender === k ? 'on' : ''}`} onClick={() => setGender(k)}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="divider" />
              <div className="fgroup">
                <span className="flabel">Joined</span>
                <div className="seg">
                  {JOINED.map(([k, l]) => (
                    <button key={k} type="button" className={`opt ${joined === k ? 'on' : ''}`} onClick={() => setJoined(k)}>{l}</button>
                  ))}
                </div>
                {joined === 'custom' && (
                  <span className="daterange">
                    <input type="date" max={TODAY} value={from} onChange={(e) => { const v = e.target.value; setFrom(v); if (v > to) setTo(v); }} />
                    <span className="dsep">to</span>
                    <input type="date" max={TODAY} value={to} onChange={(e) => { const v = e.target.value; setTo(v); if (v < from) setFrom(v); }} />
                  </span>
                )}
              </div>
              <div className="divider" />
              <div className="dd">
                <div className={`pillfilter ${country !== 'All' ? 'on' : ''}`} onClick={toggleMenu('country')}>Country: <b>{country}</b><span className="car" /></div>
                <div className={`ddmenu ${openMenu === 'country' ? 'open' : ''}`}>
                  {countries.map((c) => (
                    <div key={c.name} className={`item ${country === c.name ? 'sel' : ''}`} onClick={() => pickCountry(c.name)}>
                      {/* `!= null`, not truthiness: a country kept in the list because it
                          is the one selected can legitimately count 0 under the other
                          filters, and hiding that reads as "count unavailable". */}
                      {c.name}{c.n != null ? <span className="cnt">{c.n.toLocaleString()}</span> : null}
                    </div>
                  ))}
                </div>
              </div>
              <div className="dd">
                <div className={`pillfilter ${location !== 'All' ? 'on' : ''}`} onClick={toggleMenu('loc')}>City: <b>{location}</b><span className="car" /></div>
                <div className={`ddmenu ${openMenu === 'loc' ? 'open' : ''}`}>
                  {locations.map((l) => (
                    <div key={l.name} className={`item ${location === l.name ? 'sel' : ''}`} onClick={() => pick(setLocation)(l.name)}>
                      {l.name}{l.n != null ? <span className="cnt">{l.n.toLocaleString()}</span> : null}
                    </div>
                  ))}
                </div>
              </div>
              <div className="dd">
                <div className={`pillfilter ${status !== 'All' ? 'on' : ''}`} onClick={toggleMenu('status')}>Verified: <b>{status}</b><span className="car" /></div>
                <div className={`ddmenu ${openMenu === 'status' ? 'open' : ''}`}>
                  {['All', 'Verified'].map((l) => (<div key={l} className={`item ${status === l ? 'sel' : ''}`} onClick={() => pick(setStatus)(l)}>{l}</div>))}
                </div>
              </div>
              <div className="dd">
                <div className={`pillfilter ${account !== 'All' ? 'on' : ''}`} onClick={toggleMenu('account')}>Account: <b>{(ACCOUNTS.find(([k]) => k === account) || ACCOUNTS[0])[1]}</b><span className="car" /></div>
                <div className={`ddmenu ${openMenu === 'account' ? 'open' : ''}`}>
                  {ACCOUNTS.map(([k, l]) => (<div key={k} className={`item ${account === k ? 'sel' : ''}`} onClick={() => pick(setAccount)(k)}>{l}</div>))}
                </div>
              </div>
              <div className="dd">
                <div className="pillfilter" onClick={toggleMenu('sort')}>Sort: <b>{SORTS[sort]}</b><span className="car" /></div>
                <div className={`ddmenu ${openMenu === 'sort' ? 'open' : ''}`}>
                  {Object.entries(SORTS).map(([k, l]) => (<div key={k} className={`item ${sort === k ? 'sel' : ''}`} onClick={() => pick(setSort)(k)}>{l}</div>))}
                </div>
              </div>
              <span className="clearf" onClick={clearAll}>Clear all</span>
              <button type="button" className="export-btn" onClick={exportCsv} disabled={exporting}><span>⤓</span> {exporting ? 'Exporting…' : 'Export CSV'}</button>
            </div>

            <div className="hint"><span><b>Click any member row</b> (or “View history”) to open that member’s full activity page — every conversation and monthly volume from join date to today. Message content is never shown; admins see activity counts only.</span></div>

            <div className="tablecard stickyhead">
              <table>
                <colgroup>
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '12%' }} />
                  <col />
                </colgroup>
                {/* Every column keeps its own definition; the hover text states the SCOPE
                    each one is measured over, because the two are not the same across a
                    group. "People Messaged" / "Messaged {P.Obj}" are PEOPLE counts and are
                    opener-agnostic — a reply {P.subj} sent inside a conversation the other
                    person opened still makes them someone {P.subj} messaged. The four
                    columns beside them are CONVERSATION counts split by who sent the
                    thread's first message. Without this an admin reads "{P.subj} reached
                    out" across the whole group and takes the people count for outreach
                    (Melanie: 5 people messaged, 0 conversations opened). */}
                <thead>
                  <tr className="grp">
                    <th /><th />
                    <th className="gsent" colSpan={3} title={`Conversations ${P.subj} started, plus the people ${P.subj} has sent messages to`}><span className="gline">Sent (Outbound) — {P.subj} reached out</span></th>
                    <th className="grecv" colSpan={3} title={`Conversations the other person started, plus the people who have sent ${P.obj} messages`}><span className="gline">Received (Inbound) — {P.subj} {P.were} messaged</span></th>
                    <th />
                  </tr>
                  <tr className="cols">
                    <th>Member</th>
                    <th title="Signup date — the Joined filter above decides which members are listed; every messaging figure is all-time. The small line is their latest plan.">Joined</th>
                    {/* CONVERSATIONS, not people — one measure across the whole tab (see the
                        note on the detail page's cards). The distinct-people counts are not
                        rendered anywhere any more; don't reintroduce one into a column. */}
                    <th className="num sortable" onClick={() => setSort('oreached')} title={`Conversations ${P.subj} STARTED — threads where ${P.poss} message came first.`}>Conversations Started {sort === 'oreached' && <span className="sortc">▾</span>}</th>
                    <th className="num" title={`Of the conversations ${P.subj} STARTED, how many got at least one message back.`}>Replied To {P.Obj}</th>
                    <th className="num" title={`Replies ÷ conversations ${P.subj} started. Shows — when ${P.subj} ${P.were === 'were' ? 'have' : 'has'} started none. The small line is the median time to that first reply.`}>Reply Rate</th>
                    <th className="num sortable" onClick={() => setSort('reached')} title={`Conversations the OTHER person started with ${P.obj} — threads where their message came first.`}>Started With {P.Obj} {sort === 'reached' && <span className="sortc">▾</span>}</th>
                    <th className="num" title={`Of the conversations the OTHER person started, how many ${P.subj} answered.`}>{P.Subj} Replied</th>
                    <th className="num sortable" onClick={() => setSort('rr')} title={`Conversations answered ÷ conversations the other person started. Shows — when nobody has started one. The small line is the median time ${P.subj} ${P.were === 'were' ? 'take' : 'takes'} to reply.`}>Reply Rate {sort === 'rr' && <span className="sortc">▾</span>}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr className="emptyrow"><td colSpan={9}>No members match these filters.</td></tr>
                  ) : rows.map((m, i) => {
                    const s = m.snap || {};
                    const irep = s.irep ?? s.replied;
                    const rowP = m.gender === 'F' ? 'she' : m.gender === 'M' ? 'he' : 'they';
                    return (
                      <tr key={m.id} className={i % 2 ? 'alt' : ''} onClick={() => goDetail(m.id)}>
                        <td>
                          <div className="member">
                            <span className="ava" style={{ background: m.profilePicture ? undefined : '#c9a35e' }}>
                              {m.profilePicture ? <img src={m.profilePicture} alt="" /> : m.initials}
                              {m.verified && <span className="ava-badge" />}
                            </span>
                            <div className="m-meta">
                              <div className="nm">{m.name}{m.age ? <span className="age">, {m.age}</span> : null} {genderChip(m.gender)} {acctPill(m.account)}</div>
                              <div className="loc"><span className="pin" /> {m.city || '—'}{m.country ? `, ${m.country}` : ''}{m.joinedDaysAgo != null && m.joinedDaysAgo <= 7 ? <span className="newtag">New</span> : null}</div>
                            </div>
                          </div>
                        </td>
                        {/* Tier reads the member's LATEST subscription in any subscriber
                            state (Active / Cancelled / Expired) — an expired member used
                            to read "Free tier" here while the Subscription Report listed
                            them as a subscriber. `lapsed` keeps a cancelled plan from
                            reading as one they are still paying for. */}
                        <td className="joined">{m.joinedDaysAgo == null ? '—' : joinedLabel(m.joinedDaysAgo)}<small>{m.tier}{m.tierLapsed ? ' · lapsed' : ''}</small></td>
                        {/* `metricsOff` short-circuits every metric cell to "—". A real 0 is
                            meaningful here ("started nothing"), so it must stay
                            distinguishable from "we could not compute this". */}
                        <td className="num"><div className="metric">{metricsOff ? '—' : (s.oreached ?? 0)}<small>{metricsOff ? 'unavailable' : 'started'}</small></div></td>
                        {/* Denominator is conversations SHE OPENED (oreached), not everyone
                            she messaged (pm). Against pm this read a hard 100% for members
                            who only ever answer other people — they never message anyone
                            who hadn't messaged them first, so the old orep/pm was 1 by
                            construction. oreached === 0 means she has opened nothing, so the
                            whole cell is "—" rather than a real-looking 0%. */}
                        <td className="num"><div className="metric">{metricsOff || !s.oreached ? '—' : (s.outboundReplied ?? '—')}<small>{metricsOff ? '' : `of ${s.oreached ?? 0}`}</small></div></td>
                        <td className="num">
                          {!metricsOff && s.oreached && s.orr != null
                            ? <div className="rr"><span className={`reply ${rrClass(s.orr)}`}>{s.orr}%</span><span className="rrt">reply in {fmtMin(s.outMin)}</span></div>
                            : <span className="reply lo">—</span>}
                        </td>
                        <td className="num"><div className="metric">{metricsOff ? '—' : (s.reached ?? 0)}<small>{metricsOff ? 'unavailable' : 'inbound'}</small></div></td>
                        {/* `reached`, not `pmh` — s.rr below is replied/inboundThreads, so
                            printing it over a people count made the fraction and the
                            percentage disagree whenever a pair spans several rooms.
                            Both cells are gated on `reached` for the same reason the
                            outbound pair is gated on `oreached`: someone can have been
                            messaged (pmh > 0) inside threads SHE opened and so have no
                            inbound conversation at all. 52 members were in exactly that
                            position and each was rendering a red, wrong "0%". */}
                        <td className="num"><div className="metric">{metricsOff || !s.reached ? '—' : (irep ?? '—')}<small>{metricsOff ? '' : `of ${s.reached ?? 0}`}</small></div></td>
                        <td className="num">
                          {!metricsOff && s.reached
                            ? <div className="rr"><span className={`reply ${rrClass(s.rr)}`}>{s.rr}%</span><span className="rrt">{rowP} replies in {fmtMin(s.inMin)}</span></div>
                            : <span className="reply lo" title={metricsOff ? 'Per-member figures are unavailable right now' : 'Nobody has opened a conversation with this member, so there is no inbound reply rate'}>—</span>}
                        </td>
                        <td className="viewcell"><span className="viewbtn" onClick={(e) => { e.stopPropagation(); openDetailNewTab(m.id); }}>View history <span className="ar" /></span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="foot">
                <div className="info">
                  Showing <b>{rows.length ? (page - 1) * PAGE_SIZE + 1 : 0}–{(page - 1) * PAGE_SIZE + rows.length}</b> of <b>{total.toLocaleString()}</b> members · joined {joinedText} · all-time messaging
                </div>
                {pages > 1 && (
                  <div className="pager">
                    <span className={`pg ${page === 1 ? 'off' : ''}`} onClick={() => page > 1 && setPage(page - 1)}>‹</span>
                    {pageList.map((p, i) => (p === '…'
                      ? <span className="pg dots" key={`d${i}`}>…</span>
                      : <span key={p} className={`pg ${p === page ? 'cur' : ''}`} onClick={() => setPage(p)}>{p}</span>))}
                    <span className={`pg ${page === pages ? 'off' : ''}`} onClick={() => page < pages && setPage(page + 1)}>›</span>
                  </div>
                )}
              </div>
            </div>

            {/* Every figure on this tab is a CONVERSATION count split by who sent the
                thread's first message, plus the reply rate over it. The distinct-people
                counts are no longer shown anywhere (user's call — see MemberHistory.js).
                This note used to define the outbound rate over people, which is not what it
                has ever computed. */}
            <div className="note">Data from the live chat store · a conversation is “started” by whoever sent its first message · outbound reply rate = conversations the member started that got a reply ÷ conversations they started · inbound reply rate = conversations the member replied in ÷ conversations someone else started · profile likes from the activity log. Bot and deleted counterparties are counted and labelled. Message content is never exposed.</div>
          </>
        )}
      </div>
    </Box>
  );
};

export default MessagingActivity;
